import type { ReactNode } from 'react';
import type { DesignId, Site } from '../model/types';
import { firstVisibleOfType } from '../model/catalog';
import { fmtDate, fmtTime } from '../model/format';
import { MONTHS, initials, parseDate } from '../lib/util';
import { hasPhoto } from '../lib/photos';
import { placeholderURI, wreathURI } from './art';
import { themeFor, type SiteMode } from './theme';
import { EditChip, Photo, type EditTarget } from './parts';

interface Props { site: Site; designId?: DesignId; mode: SiteMode; onEdit?: (t: EditTarget) => void; onRsvp?: (blockId: string) => void }

const Amp = () => <i>&amp;</i>;
const join = (parts: string[], sep: string) => parts.filter(Boolean).join(sep);

export function Hero({ site: s, designId, mode, onEdit, onRsvp }: Props) {
  const h = s.header;
  const id = designId || s.design.base;
  const n1 = h.name1 || 'Partner one', n2 = h.name2 || 'Partner two';
  const intro = h.intro || '';
  const dateTxt = fmtDate(h.date, s.design.dateFormat);
  const timeTxt = fmtTime(h.time, s.design.timeFormat);
  const venue = h.venue || '', city = h.city || '';
  const place = join([venue, city], ', ');
  const { colors } = themeFor(s, designId);
  const rsvp = mode === 'thumb' ? null : firstVisibleOfType(s, 'rsvp');
  // Thumbnails sit inside clickable cards, so they draw the button as plain text
  const rsvpBtn: ReactNode = mode === 'thumb'
    ? <span className="s-btn">RSVP</span>
    : rsvp ? <button type="button" className="s-btn" onClick={() => onRsvp?.(rsvp.id)}>RSVP</button> : null;
  const editable = mode === 'edit' ? { 'data-edit': '' } : {};
  const chip = <EditChip mode={mode} label="Edit header" onEdit={onEdit} target={{ kind: 'header' }} />;
  const heroPhoto = (fallback: number) => {
    const gal = firstVisibleOfType(s, 'gallery');
    const p = gal && gal.data.images && gal.data.images[0];
    return hasPhoto(p) ? <Photo p={p} width={1400} /> : <img src={placeholderURI(fallback, colors)} alt="" />;
  };
  const line = (cls: string, sep: string) => <p className={cls}>{join([timeTxt, place], sep)}</p>;

  if (id === 'minimal') {
    return (
      <header className="s-hero hero-minimal" {...editable}>{chip}
        {intro && <p className="hm-intro">{intro}</p>}
        <h1 className="hm-names"><span>{n1}</span><span><i>&amp;</i> {n2}</span></h1>
        <div className="hm-meta">
          <div><span className="hm-k">When</span><span>{dateTxt || ' '}</span><span>{timeTxt}</span></div>
          <div><span className="hm-k">Where</span><span>{venue || ' '}</span><span>{city}</span></div>
          {rsvpBtn && <div className="hm-cta">{rsvpBtn}</div>}
        </div>
      </header>
    );
  }
  if (id === 'arch') {
    return (
      <header className="s-hero hero-arch" {...editable}>{chip}
        <div className="ha-frame">{heroPhoto(3)}</div>
        <div className="ha-text">
          {intro && <p className="ha-intro">{intro}</p>}
          <h1 className="ha-names"><span>{n1}</span><Amp /><span>{n2}</span></h1>
          {dateTxt && <p className="ha-date">{dateTxt}</p>}
          {line('ha-place', ' at ')}
          {rsvpBtn}
        </div>
      </header>
    );
  }
  if (id === 'monogram') {
    return (
      <header className="s-hero hero-mono" {...editable}>{chip}
        <div className="hmo-seal" aria-hidden="true"><span>{initials(h.name1)[0]}</span><Amp /><span>{initials(h.name2)[0]}</span></div>
        {intro && <p className="hmo-intro">{intro}</p>}
        <h1 className="hmo-names">{n1} <Amp /> {n2}</h1>
        <div className="hmo-rule" />
        {dateTxt && <p className="hmo-date">{dateTxt}{timeTxt ? `, ${timeTxt}` : ''}</p>}
        <p className="hmo-place">{place}</p>
      </header>
    );
  }
  if (id === 'deco') {
    const corner = (
      <svg viewBox="0 0 46 46" fill="none" stroke="currentColor" strokeWidth={1}>
        <path d="M1 45V1h44M7 39V7h32M1 13h6M13 1v6" /><rect x="10" y="10" width="6" height="6" transform="rotate(45 13 13)" />
      </svg>
    );
    return (
      <header className="s-hero hero-deco" {...editable}>{chip}
        <div className="hd-frame">
          <span className="hd-corner tl">{corner}</span><span className="hd-corner tr">{corner}</span>
          <span className="hd-corner bl">{corner}</span><span className="hd-corner br">{corner}</span>
          <svg className="hd-fan" viewBox="0 0 120 62" fill="none" stroke="currentColor" strokeWidth={1}>
            {Array.from({ length: 13 }, (_, k) => {
              const a = Math.PI - (k / 12) * Math.PI;
              return <path key={k} d={`M60 60 L${(60 + Math.cos(a) * 58).toFixed(1)} ${(60 - Math.sin(a) * 58).toFixed(1)}`} />;
            })}
            <path d="M2 60 A58 58 0 0 1 118 60M22 60 A38 38 0 0 1 98 60" />
          </svg>
          {intro && <p className="hd-intro">{intro}</p>}
          <h1 className="hd-names"><span>{n1}</span><Amp /><span>{n2}</span></h1>
          {dateTxt && <p className="hd-date">{dateTxt}</p>}
          {line('hd-place', ', ')}
        </div>
      </header>
    );
  }
  if (id === 'wreath') {
    return (
      <header className="s-hero hero-wreath" {...editable}>{chip}
        <div className="hw-wreath"><img className="hw-ring" src={wreathURI(colors)} alt="" />
          <div className="hw-inner">{intro && <p className="hw-intro">{intro}</p>}<h1 className="hw-names"><span>{n1}</span><Amp /><span>{n2}</span></h1></div>
        </div>
        {dateTxt && <p className="hw-date">{dateTxt}</p>}
        {line('hw-place', ', ')}
        {rsvpBtn}
      </header>
    );
  }
  if (id === 'editorial') {
    const p = parseDate(h.date);
    return (
      <header className="s-hero hero-editorial" {...editable}>{chip}
        <div className="he-text">
          {intro && <p className="he-intro">{intro}</p>}
          <h1 className="he-names"><span>{n1}</span><span className="amp">&amp;</span><span>{n2}</span></h1>
          {p && <div className="he-date"><b>{p.d}</b><span>{MONTHS[p.m]}<br />{p.y}</span></div>}
          {line('he-place', ', ')}
          {rsvpBtn}
        </div>
        <figure className="he-photo">{heroPhoto(0)}</figure>
      </header>
    );
  }
  if (id === 'coastal') {
    return (
      <header className="s-hero hero-coastal" {...editable}>{chip}
        <span className="hc-sun" aria-hidden="true" />
        <div className="hc-text">
          {intro && <p className="hc-intro">{intro}</p>}
          <h1 className="hc-names">{n1} <Amp /> {n2}</h1>
          {dateTxt && <p className="hc-date">{dateTxt}</p>}
          {line('hc-place', ', ')}
          {rsvpBtn}
        </div>
        <svg className="hc-waves" viewBox="0 0 1200 180" preserveAspectRatio="none" aria-hidden="true">
          <path d="M0 62 C150 36 300 92 450 64 S750 36 900 64 S1100 92 1200 62 V180 H0Z" style={{ fill: 'var(--accent)', opacity: 0.2 }} />
          <path d="M0 96 C200 72 350 122 600 96 S1000 72 1200 100 V180 H0Z" style={{ fill: 'color-mix(in srgb,var(--accent) 45%,var(--bg))' }} />
          <path d="M0 132 C250 114 450 150 700 132 S1050 116 1200 134 V180 H0Z" style={{ fill: 'var(--bg)' }} />
        </svg>
      </header>
    );
  }
  if (id === 'indigo') {
    return (
      <header className="s-hero hero-indigo" {...editable}>{chip}
        <div className="hi-band" aria-hidden="true" />
        <div className="hi-text">
          {intro && <p className="hi-intro">{intro}</p>}
          <h1 className="hi-names"><span>{n1}</span><Amp /><span>{n2}</span></h1>
          <div className="hi-rule" aria-hidden="true" />
          {dateTxt && <p className="hi-date">{dateTxt}</p>}
          {line('hi-place', ', ')}
          {rsvpBtn}
        </div>
        <div className="hi-band" aria-hidden="true" />
      </header>
    );
  }
  if (id === 'confetti') {
    return (
      <header className="s-hero hero-confetti" {...editable}>{chip}
        <div className="hf-shapes" aria-hidden="true">
          <i className="cf s1" /><i className="cf s2" /><i className="cf s3" /><i className="cf s4" /><i className="cf s5" />
          <svg className="cf s6" viewBox="0 0 120 40" aria-hidden="true"><path d="M4 20 Q19 2 34 20 T64 20 T94 20 T116 20" fill="none" style={{ stroke: 'var(--c2)' }} strokeWidth={7} strokeLinecap="round" /></svg>
          <i className="cf s7" /><i className="cf s8" />
        </div>
        <div className="hf-text">
          {intro && <p className="hf-intro">{intro}</p>}
          <h1 className="hf-names"><span>{n1}</span><span className="hf-amp">&amp;</span><span>{n2}</span></h1>
          {dateTxt && <p className="hf-date">{dateTxt}</p>}
          {line('hf-place', ', ')}
          {rsvpBtn}
        </div>
      </header>
    );
  }
  // Black Tie (default)
  return (
    <header className="s-hero hero-blacktie" {...editable}>{chip}
      <div className="bt-inner">
        {intro && <p className="bt-intro">{intro}</p>}
        <h1 className="bt-names"><span>{n1}</span><Amp /><span>{n2}</span></h1>
        {dateTxt && <p className="bt-date"><span>{dateTxt}</span></p>}
        {(timeTxt || place) && line('bt-place', ' · ')}
        {rsvpBtn}
      </div>
    </header>
  );
}

export function Ornament({ site: s }: { site: Site }) {
  const id = s.design.base;
  const wrap = (svg: ReactNode, cls = 's-orn') => <div className={cls} aria-hidden="true">{svg}</div>;
  switch (id) {
    case 'blacktie':
      return wrap(<svg width="22" height="22" viewBox="0 0 22 22"><path d="M11 0 C12 7 15 10 22 11 C15 12 12 15 11 22 C10 15 7 12 0 11 C7 10 10 7 11 0Z" fill="currentColor" /></svg>);
    case 'monogram':
      return wrap(<svg width="120" height="20" viewBox="0 0 120 20" fill="none" stroke="currentColor"><path d="M0 10h44M76 10h44" /><circle cx="60" cy="10" r="7" /><circle cx="60" cy="10" r="2" fill="currentColor" /></svg>);
    case 'deco':
      return wrap(<svg width="140" height="22" viewBox="0 0 140 22" fill="none" stroke="currentColor"><path d="M0 11h52M88 11h52M0 7h40M100 7h40" /><rect x="62" y="3" width="16" height="16" transform="rotate(45 70 11)" /><rect x="66" y="7" width="8" height="8" transform="rotate(45 70 11)" fill="currentColor" /></svg>);
    case 'wreath':
      return wrap(
        <svg width="110" height="34" viewBox="0 0 110 34">
          <path d="M55 30 C40 26 22 18 8 6 M55 30 C70 26 88 18 102 6" stroke="#7E9A76" strokeWidth={1.5} fill="none" />
          {[[16, 12, -28], [26, 18, -24], [36, 23, -18], [46, 27, -10]].map(([x, y, r]) => (
            <g key={x}>
              <ellipse cx={x} cy={y} rx="7" ry="3" transform={`rotate(${r} ${x} ${y})`} fill="#9DB394" />
              <ellipse cx={110 - x} cy={y} rx="7" ry="3" transform={`rotate(${-r} ${110 - x} ${y})`} fill="#9DB394" />
            </g>
          ))}
          <circle cx="55" cy="30" r="3.5" style={{ fill: 'var(--accent)' }} />
        </svg>,
      );
    case 'coastal':
      return wrap(<svg width="120" height="16" viewBox="0 0 120 16" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round"><path d="M2 8 Q12 1 22 8 T42 8 T62 8 T82 8 T102 8 T118 8" /></svg>);
    case 'indigo':
      return wrap(<span className="s-orn-band" />);
    case 'confetti':
      return wrap(<><i /><i /><i /></>, 's-orn s-orn-dots');
    case 'arch':
      return wrap(<svg width="34" height="40" viewBox="0 0 34 40" fill="none" stroke="currentColor" strokeWidth={1.2}><path d="M2 39V17a15 15 0 0 1 30 0v22" /><path d="M8 39V18a9 9 0 0 1 18 0v21" /></svg>);
    default:
      return null;
  }
}
